import React, { useState, useEffect } from 'react';
import { InductionForm } from './components/InductionForm';
import { AdminDashboard } from './components/AdminDashboard';
import { MainMenu } from './components/MainMenu';
import { MandatoryTraining } from './components/MandatoryTraining';
import { ViewState, UserRole } from './types';
import { Lock, User, Loader2, ArrowLeft } from 'lucide-react';
import { auth } from './services/firebase';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';

const App: React.FC = () => {
  const [view, setView] = useState<ViewState>('MAIN_MENU');
  const [lang, setLang] = useState<'id' | 'en'>(() => (localStorage.getItem('lang') as 'id' | 'en') || 'id');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [currentRole, setCurrentRole] = useState<UserRole | null>(null);
  const [loginError, setLoginError] = useState('');
  const [authLoading, setAuthLoading] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const handleLangIdx = (selectedLang: 'id' | 'en') => {
    setLang(selectedLang);
    localStorage.setItem('lang', selectedLang);
  };

  // Ensure all questions are initialized and available in Firebase Firestore on startup
  useEffect(() => {
    import('./services/storageService').then(({ getMandatorySettings, getQuestions, getPostTestQuestions }) => {
      getMandatorySettings().catch(() => {});
      getQuestions().catch(() => {});
      getPostTestQuestions().catch(() => {});
    });
  }, []);

  // Listen to Authentication State Changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Logged in - fetch assigned role if exists in database, fallback to EDITORS
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('./services/firebase');
          const docRef = doc(db, 'admins', user.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists() && docSnap.data()?.role) {
            setCurrentRole(docSnap.data().role as UserRole);
          } else {
            setCurrentRole('EDITOR');
          }
        } catch (error) {
          console.error("Gagal mengambil peran admin dari database:", error);
          setCurrentRole('EDITOR'); 
        }
        setView('ADMIN_DASHBOARD');
      } else {
        // Logged out
        setCurrentRole(null);
        // Don't force redirect to FORM if user is explicitly on ADMIN_LOGIN page
        if (view === 'ADMIN_DASHBOARD') {
          setView('ADMIN_LOGIN');
        }
      }
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, [view]);

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setIsLoggingIn(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      // View transition is handled by onAuthStateChanged
      setEmail('');
      setPassword('');
    } catch (error: any) {
      console.error("Login Error:", error);
      let errorMessage = "Gagal login. Periksa email dan password.";
      
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
        errorMessage = "Email atau password salah.";
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = "Terlalu banyak percobaan. Silakan coba lagi nanti.";
      }
      
      setLoginError(errorMessage);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setView('ADMIN_LOGIN');
    } catch (error) {
      console.error("Logout Error:", error);
    }
  };

  // Switch View Helper
  const goToAdminLogin = () => {
    if (currentRole) {
      setView('ADMIN_DASHBOARD');
    } else {
      setView('ADMIN_LOGIN');
    }
  };

  const renderContent = () => {
    // Show global loading state if checking auth
    if (authLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
        </div>
      );
    }

    switch (view) {
      case 'MAIN_MENU':
        return (
          <MainMenu
            lang={lang}
            onSelectInduction={() => setView('FORM')}
            onSelectMandatoryTraining={() => setView('MANDATORY_TRAINING')}
            onSelectMandatory={() => setView('MANDATORY_TRAINING')}
            onSelectAdmin={goToAdminLogin}
            onToggleLang={handleLangIdx}
          />
        );

      case 'MANDATORY_TRAINING':
        return (
          <div className="min-h-screen bg-gray-100 flex flex-col">
            <header className="bg-white shadow-sm mb-6">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <img src="https://github.com/chairulbreaks06-NR/foto-eratools/blob/main/logo%20ehs%20mkt.png?raw=true" alt="UT Logo" className="h-10 sm:h-12" onError={(e) => (e.currentTarget.src = 'https://via.placeholder.com/100x40?text=Logo')} />
                  <span className="font-bold text-gray-800 text-sm sm:text-base ml-2 hidden sm:block">Safety Mandatory Training Portal</span>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="bg-gray-100 p-1 rounded-lg flex items-center border border-gray-200">
                    <button
                      onClick={() => handleLangIdx('id')}
                      className={`px-3 py-1 rounded-md text-xs font-bold transition-all cursor-pointer ${
                        lang === 'id' ? 'bg-amber-600 text-white shadow-xs' : 'text-gray-500 hover:text-gray-800'
                      }`}
                    >
                      ID
                    </button>
                    <button
                      onClick={() => handleLangIdx('en')}
                      className={`px-3 py-1 rounded-md text-xs font-bold transition-all cursor-pointer ${
                        lang === 'en' ? 'bg-amber-600 text-white shadow-xs' : 'text-gray-500 hover:text-gray-800'
                      }`}
                    >
                      EN
                    </button>
                  </div>

                  <button 
                    onClick={goToAdminLogin}
                    className="text-gray-500 hover:text-amber-600 flex items-center gap-1.5 text-xs font-semibold transition-colors"
                  >
                    <Lock size={14} />
                    <span className="hidden sm:inline">Admin</span>
                  </button>
                </div>
              </div>
            </header>

            <div className="flex-grow">
              <MandatoryTraining lang={lang} onBackToMain={() => setView('MAIN_MENU')} />
            </div>

            <footer className="w-full text-center py-6 text-xs text-gray-400 border-t border-gray-200 mt-10 bg-white select-none font-medium">
              Aplikasi By IRL92
            </footer>
          </div>
        );

      case 'FORM':
        return (
          <div className="min-h-screen bg-gray-100 flex flex-col">
            {/* Simple Top Bar */}
            <header className="bg-white shadow-sm mb-8">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setView('MAIN_MENU')}
                    className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 transition flex items-center gap-1 text-xs font-bold cursor-pointer"
                    title="Kembali ke Menu Utama"
                  >
                    <ArrowLeft size={16} />
                    <span className="hidden sm:inline">Menu Utama</span>
                  </button>
                  <img src="https://github.com/chairulbreaks06-NR/foto-eratools/blob/main/logo%20ehs%20mkt.png?raw=true" alt="UT Logo" className="h-10 sm:h-12" onError={(e) => (e.currentTarget.src = 'https://via.placeholder.com/100x40?text=Logo')} />
                  <span className="font-bold text-gray-700 ml-1 hidden md:block">Form Safety Induksi</span>
                </div>
                
                <div className="flex items-center gap-4">
                  {/* Language Selector Toggle */}
                  <div className="bg-gray-100 p-1 rounded-lg flex items-center border border-gray-200 shadow-inner">
                    <button
                      onClick={() => handleLangIdx('id')}
                      className={`px-3 py-1 rounded-md text-xs font-bold transition-all cursor-pointer ${
                        lang === 'id'
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-gray-500 hover:text-gray-800'
                      }`}
                    >
                      ID / IND
                    </button>
                    <button
                      onClick={() => handleLangIdx('en')}
                      className={`px-3 py-1 rounded-md text-xs font-bold transition-all cursor-pointer ${
                        lang === 'en'
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-gray-500 hover:text-gray-800'
                      }`}
                    >
                      EN / ENG
                    </button>
                  </div>

                  <button 
                    onClick={goToAdminLogin}
                    className="text-gray-500 hover:text-blue-600 flex items-center gap-1.5 text-sm font-semibold transition-colors"
                  >
                    <Lock size={15} />
                    <span>Admin</span>
                  </button>
                </div>
              </div>
            </header>
            
            <div className="container mx-auto px-4 flex-grow">
              <InductionForm lang={lang} />
            </div>

            {/* Footer */}
            <footer className="w-full text-center py-6 text-xs text-gray-400 border-t border-gray-200 mt-10 bg-white select-none font-medium">
              Aplikasi By IRL92
            </footer>
          </div>
        );

      case 'ADMIN_LOGIN':
        return (
          <div className="min-h-screen bg-gray-100 flex flex-col justify-between p-4">
            <div className="flex-grow flex items-center justify-center">
              <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
              <div className="text-center mb-6">
                <div className="bg-gray-100 p-3 rounded-full inline-block mb-4">
                  <Lock className="w-8 h-8 text-gray-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Admin Login</h2>
              </div>
              <form onSubmit={handleAdminLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input 
                      type="email" 
                      className="w-full border border-gray-300 rounded-lg pl-10 p-3 outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input 
                      type="password" 
                      className="w-full border border-gray-300 rounded-lg pl-10 p-3 outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                </div>
                {loginError && (
                  <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg text-center border border-red-200">
                    {loginError}
                  </div>
                )}
                <button 
                  type="submit" 
                  disabled={isLoggingIn}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition flex justify-center items-center gap-2 disabled:opacity-70"
                >
                  {isLoggingIn && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isLoggingIn ? 'Logging in...' : 'Login'}
                </button>
                <button 
                  type="button" 
                  onClick={() => setView('MAIN_MENU')}
                  className="w-full text-gray-500 py-2 text-sm hover:underline mt-2"
                >
                  ← Kembali ke Menu Utama
                </button>
              </form>
            </div>
          </div>

          {/* Footer */}
          <footer className="w-full text-center py-4 text-xs text-gray-400 select-none font-medium">
            Aplikasi By IRL92
          </footer>
        </div>
        );

      case 'ADMIN_DASHBOARD':
        // Double check auth just in case
        if (!currentRole) return null; 
        return <AdminDashboard role={currentRole} onLogout={handleLogout} />;
    }
  };

  return renderContent();
};

export default App;